/**
 * UUIDv7 — time-ordered, client-generated, collision-free without coordination.
 *
 * Every id in Fanni that isn't an invoice number is minted here, on whatever device
 * created the thing. That is what lets a technician create a job, add materials to it,
 * and sign it while offline, with no server round-trip to allocate anything.
 *
 * v7 (not v4) because the first 48 bits are a millisecond timestamp, so ids sort by
 * creation time. That keeps Postgres b-tree inserts append-y instead of scattering
 * writes across the index, and it means "ORDER BY id" is a usable tiebreak in the
 * keyset cursor (see SYNC.md, Rule 6).
 */

const HEX: string[] = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, "0"));

export function uuidv7(now: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // 48-bit big-endian millisecond timestamp
  const ts = BigInt(now);
  bytes[0] = Number((ts >> 40n) & 0xffn);
  bytes[1] = Number((ts >> 32n) & 0xffn);
  bytes[2] = Number((ts >> 24n) & 0xffn);
  bytes[3] = Number((ts >> 16n) & 0xffn);
  bytes[4] = Number((ts >> 8n) & 0xffn);
  bytes[5] = Number(ts & 0xffn);

  bytes[6] = (bytes[6] & 0x0f) | 0x70; // version 7
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant RFC 4122

  const h = HEX;
  return (
    h[bytes[0]] + h[bytes[1]] + h[bytes[2]] + h[bytes[3]] + "-" +
    h[bytes[4]] + h[bytes[5]] + "-" +
    h[bytes[6]] + h[bytes[7]] + "-" +
    h[bytes[8]] + h[bytes[9]] + "-" +
    h[bytes[10]] + h[bytes[11]] + h[bytes[12]] + h[bytes[13]] + h[bytes[14]] + h[bytes[15]]
  );
}

export function timestampOf(id: string): number {
  return Number(BigInt("0x" + id.slice(0, 8) + id.slice(9, 13)));
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
export const isUuidv7 = (v: string): boolean => UUID_RE.test(v);
